// get phone model
import * as Sentry from "@sentry/vue";
import { UAParser } from "ua-parser-js";
import { getDeviceVendor } from "ua-parser-js/helpers";
import { detectWeChatMiniProgram } from "./useWeChatSDK.js";

export class DeviceInfo {
  constructor(
    model = undefined,
    vendor = undefined,
    deviceType = undefined,
    isMobile = undefined,
    os = undefined,
    osVersion = undefined,
    browser = undefined,
    browserVersion = undefined,
    browserMajorVersion = undefined,
    browserEngine = undefined,
    browserEngineVersion = undefined,
    isMiniProgram = false
  ) {
    this.model = model;
    this.vendor = vendor;
    this.deviceType = deviceType;
    this.os = os;
    this.osVersion = osVersion;
    this.browser = browser;
    this.browserVersion = browserVersion;
    this.browserMajorVersion = browserMajorVersion;
    this.browserEngine = browserEngine;
    this.browserEngineVersion = browserEngineVersion;
    this.isMiniProgram = isMiniProgram;
  }

  getOS() {
    return this.os?.toLowerCase();
  }

  getVendor() {
    return this.vendor?.toLowerCase();
  }

  getModel() {
    return this.model;
  }

  isMobileOrTablet() {
    const type = this.deviceType?.toLowerCase();
    return type === "mobile" || type === "tablet";
  }

  getOSVersion() {
    return this.osVersion;
  }

  getBrowser() {
    return this.browser?.toLowerCase();
  }

  getBrowserVersion() {
    return this.browserVersion;
  }

  getBrowserMajorVersion() {
    return this.browserMajorVersion;
  }

  getBrowserEngine() {
    return this.browserEngine?.toLowerCase();
  }

  getBrowserEngineVersion() {
    return this.browserEngineVersion;
  }

  isChromiumBased() {
    return this.browserEngine?.toLowerCase() === "blink";
  }

  isIos() {
    return this.os?.toLowerCase() === "ios" || (this.vendor?.toLowerCase() === "apple" && this.isMobileOrTablet());
  }

  isAndroid() {
    return this.os?.toLowerCase() === "android";
  }

  isWechat() {
    return this.browser?.toLowerCase() === "wechat" || this.isMiniProgram;
  }

  isWechatMiniProgram() {
    return this.isMiniProgram;
  }
}

export default async function getDeviceInfo() {
  const deviceInfo = new DeviceInfo();
  try {
    const isWechatMiniProgram = await detectWeChatMiniProgram();

    const uap = new UAParser();
    const result = await uap.getResult().withClientHints();
    // If the device name is not known, we can still use default settings
    deviceInfo.model = result.device.model ?? "unknown";
    deviceInfo.vendor = getDeviceVendor(result.device.model);
    deviceInfo.deviceType = result.device.type;
    deviceInfo.os = result.os.name;
    deviceInfo.osVersion = result.os.version;
    deviceInfo.isMiniProgram = isWechatMiniProgram;
    deviceInfo.browser = isWechatMiniProgram ? "wechat-miniprogram" : result.browser.name;
    deviceInfo.browserVersion = result.browser.version;
    deviceInfo.browserMajorVersion = result.browser.major;
    deviceInfo.browserEngine = result.engine.name;
    deviceInfo.browserEngineVersion = result.engine.version;

    Sentry.setTag("wechat", deviceInfo.isWechat());
    Sentry.setTag("wechat-miniprogram", isWechatMiniProgram);
  } catch (error) {
    const sentryError = new Error(`Error while getting device info: ${error.message}`);
    Sentry.captureException(sentryError);
  }
  return deviceInfo;
}
